import * as ko from 'knockout';
import { GameBase, type GameBaseOptions } from '../../shared/models/game-base';
import type { ITeam } from '../../shared/interfaces/ITeam';
import { SelectionType } from '../../shared/enums/selection-type';
import { DateTime } from 'luxon';
import { formatLongDate } from '../../shared/date-helpers';

/**
 * MLB Game.
 * - No TIE logic (AWAY_WIN/HOME_WIN only).
 * - gameDate uses DateTime.fromISO(..., {zone:'utc'}).setZone(timezone) for timezone-aware display.
 * - startTimeTBD: when true, shifts gameDate +1 day and shows "[TBD]" prefix.
 * - gameDateShortDisplay shows "dd[*]" when startTimeTBD.
 * - gameDateLongDisplay uses "z" timezone token for timezone abbreviation.
 */
export class MlbGame extends GameBase {
  startTimeTBD: boolean;

  constructor(
    home: KnockoutObservable<ITeam> | ITeam,
    away: KnockoutObservable<ITeam> | ITeam,
    week: number,
    gameScheduleNum?: number,
    gameDate?: string,
    gameGuid?: string,
    gameKey?: string,
    timezone?: string,
    startTimeTbd?: boolean
  ) {
    // Pass null gameDate to base — we handle it ourselves (needs timezone)
    const opts: GameBaseOptions = {
      home: home as KnockoutObservable<ITeam>,
      away: away as KnockoutObservable<ITeam>,
      week,
      gameScheduleNum,
      gameGuid,
      gameKey,
    };
    super(opts);

    this.startTimeTBD = startTimeTbd ?? false;

    // MLB uses UTC→timezone conversion for accurate local time display
    if (gameDate) {
      let gamedate = DateTime.fromISO(gameDate, { zone: 'utc' }).setZone(timezone ?? 'America/New_York');
      if (this.startTimeTBD) {
        gamedate = gamedate.plus({ days: 1 });
      }
      this.gameDate(gamedate);
    }

    // Reassign KO computeds set in base to override MLB-specific date formatting.
    // Base creates these as ko.pureComputed; we replace them after super().
    const startTimeTBD = this.startTimeTBD;
    this.gameDateShortDisplay = ko.pureComputed(() => {
      if (startTimeTBD) {
        return this.gameDate() ? this.gameDate().toFormat('ccc').slice(0, 2) + '*' : '';
      }
      if (this.gameDate()) {
        return this.gameDate().toFormat('ccc').slice(0, 2);
      }
      return '';
    });

    this.gameDateLongDisplay = ko.pureComputed(() => {
      if (this.gameDate()) {
        const base = formatLongDate(this.gameDate()) + ' ' + this.gameDate().toFormat('ZZZZ');
        if (startTimeTBD) {
          return 'TBD<br/> ' + base;
        }
        return base;
      }
      return '';
    });
  }

  protected override createGameClass(): string {
    let retStr = '';
    if (this.selectionMade() && this.TeamThatWon()) {
      retStr += 'selection-made ';
      if (this.selectionType() !== SelectionType.NONE) {
        retStr += this.TeamThatWon()().TeamAbbreviation;
      }
    }
    return retStr;
  }

  protected override createTeamThatWon(): KnockoutObservable<ITeam> | null {
    if (this.selectionMade() && this.selectionType() !== SelectionType.NONE) {
      switch (this.selectionType()) {
        case SelectionType.AWAY_WIN:
          return this.away;
        case SelectionType.HOME_WIN:
          return this.home;
        default:
          return null;
      }
    }
    return null;
  }

  protected override createTeamThatLost(): KnockoutObservable<ITeam> | null {
    if (this.selectionMade() && this.selectionType() !== SelectionType.NONE) {
      switch (this.selectionType()) {
        case SelectionType.AWAY_WIN:
          return this.home;
        case SelectionType.HOME_WIN:
          return this.away;
        default:
          return null;
      }
    }
    return null;
  }
}
